import { useState } from "react";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";

export default function DataGrid({
  columns,
  data,
  onCellEdit,
  loading = false,
  emptyMessage = "Aucun élément trouvé",
  rowSelection = false,
  onSelectionChange,
  className = "",
}) {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);

  const startEdit = (rowId, columnKey, currentValue) => {
    setEditingCell({ rowId, columnKey });
    setEditValue(currentValue ?? "");
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue("");
  };

  const saveEdit = async (row) => {
    if (editingCell && onCellEdit) {
      const success = await onCellEdit(
        row.id,
        editingCell.columnKey,
        editValue
      );

      if (success !== false) {
        setEditingCell(null);
        setEditValue("");
      }
    }
  };

  const handleSelectAll = (checked) => {
    const newSelection = checked ? data.map((row) => row.id) : [];
    setSelectedRows(newSelection);
    onSelectionChange?.(newSelection);
  };

  const handleSelectRow = (rowId, checked) => {
    const newSelection = checked
      ? [...selectedRows, rowId]
      : selectedRows.filter((id) => id !== rowId);
    setSelectedRows(newSelection);
    onSelectionChange?.(newSelection);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      saveEdit(data.find((d) => d.id === editingCell?.rowId));
    } else if (e.key === "Escape") {
      cancelEdit();
    }
  };

  const renderCell = (row, column) => {
    const isEditing =
      editingCell?.rowId === row.id && editingCell?.columnKey === column.key;

    const cellValue = row[column.key];

    // Cellule en cours d'édition
    if (column.editable && isEditing) {
      return (
        <EditableCell
          type={column.type || "text"}
          value={editValue}
          onChange={setEditValue}
          onSave={() => saveEdit(row)}
          onCancel={cancelEdit}
          onKeyDown={handleKeyDown}
          options={column.options}
          min={column.min}
          max={column.max}
          step={column.step}
        />
      );
    }

    // Cellule avec render custom
    if (column.render) {
      return column.render(row, {
        startEdit: () => startEdit(row.id, column.key, row[column.key]),
        isEditing,
      });
    }

    // Cellule éditable (non active)
    if (column.editable) {
      const displayValue = column.format
        ? column.format(cellValue, row)
        : cellValue;

      return (
        <div
          className="cursor-pointer hover:bg-base-200 p-2 rounded transition-colors"
          onClick={() => startEdit(row.id, column.key, cellValue)}
          title="Cliquer pour modifier"
        >
          {displayValue ?? <span className="text-gray-400">—</span>}
        </div>
      );
    }

    // Cellule normale (non éditable)
    return column.format ? column.format(cellValue, row) : cellValue;
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-gray-500">
        <div className="loading loading-spinner loading-lg"></div>
        <p className="mt-2">Chargement...</p>
      </div>
    );
  }

  if (data.length === 0) {
    return <div className="text-center py-8 text-gray-500">{emptyMessage}</div>;
  }

  return (
    <div
      className={`overflow-x-auto border rounded-lg bg-base-100 shadow-sm ${className}`}
    >
      <table className="table table-zebra w-full">
        <thead>
          <tr>
            {rowSelection && (
              <th className="w-12">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={
                    selectedRows.length === data.length && data.length > 0
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.key}
                className={column.headerClassName}
                style={{ width: column.width }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={row.id || idx}>
              {rowSelection && (
                <td>
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={selectedRows.includes(row.id)}
                    onChange={(e) => handleSelectRow(row.id, e.target.checked)}
                  />
                </td>
              )}
              {columns.map((column) => (
                <td key={column.key} className={column.cellClassName}>
                  {renderCell(row, column)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Composant pour cellule éditable
function EditableCell({
  type,
  value,
  onChange,
  onSave,
  onCancel,
  onKeyDown,
  options,
  min,
  max,
  step,
}) {
  return (
    <div className="flex items-center gap-1">
      {type === "select" ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          className="select select-sm select-bordered w-full max-w-xs"
          autoFocus
        >
          {options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : type === "checkbox" ? (
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => {
            onChange(e.target.checked);
            setTimeout(() => onSave(), 0);
          }}
          className="checkbox checkbox-sm"
          autoFocus
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          min={min}
          max={max}
          step={step}
          className="input input-sm input-bordered w-full"
          autoFocus
        />
      )}

      <button
        type="button"
        onClick={onSave}
        className="btn btn-ghost btn-xs text-green-600 p-1"
        title="Sauvegarder"
      >
        <CheckIcon className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={onCancel}
        className="btn btn-ghost btn-xs text-red-600 p-1"
        title="Annuler"
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  );
}
